<template>
	<fieldset :disabled="loading > 0" class="goal-settings">
		<form @submit.prevent.stop="submitGoal">
			<b-form-group class="mb-2">
				<b-input-group size="sm">
					<b-form-input type="number" v-model.number="goal" min="0" :step="numberStep" />
					<b-input-group-append>
						<b-input-group-text>{{ unitText }}</b-input-group-text>
						<b-button type="submit" variant="primary">Set goal</b-button>
					</b-input-group-append>
				</b-input-group>
			</b-form-group>
		</form>
		<form @submit.prevent.stop="submitCount">
			<b-form-group class="mb-2">
				<b-input-group size="sm">
					<b-form-input type="number" no-wheel v-model.number="count" min="0" :step="numberStep" />
					<b-input-group-append>
						<b-input-group-text>{{ unitText }}</b-input-group-text>
						<b-button type="submit" variant="primary">Set progress</b-button>
					</b-input-group-append>
				</b-input-group>
			</b-form-group>
		</form>
		<form @submit.prevent.stop="submitAdd">
			<b-form-group class="mb-0">
				<b-input-group size="sm">
					<b-form-input type="number" v-model.number="countAdd" :step="numberStep" />
					<b-input-group-append>
						<b-input-group-text>{{ unitText }}</b-input-group-text>
						<b-button type="submit" variant="secondary" :disabled="!countAdd">Add</b-button>
					</b-input-group-append>
				</b-input-group>
			</b-form-group>
		</form>
	</fieldset>
</template>

<script>
export default {
	props: {
		// API slug (widgetSettings/<widgetType>/...) and the widgetType matched in
		// widgetSettingsFieldChange socket messages, e.g. 'follower-goal', 'gift-goal'.
		widgetType: { type: String, required: true },
		// Used only in error toasts, e.g. 'follower goal', 'dono goal'.
		entityName: { type: String, required: true },
		// API field name for the "current count" input — 'count' for follower/sub goals,
		// 'amount' for the currency-based dono goal.
		countField: { type: String, default: 'count' },
		// Static unit text shown after each input (e.g. 'followers'). Leave unset to show
		// this user's currency code instead (the dono goal's unit).
		unit: { type: String, default: null },
		// 'int' (default) parses with parseInt and leaves the number input's step at 1.
		// 'float' parses with parseFloat and sets step="any", for the currency-based goal.
		numberType: { type: String, default: 'int' },
		// The dono goal quirk: its "amount" loads through Math.round(parseFloat(...))
		// instead of the plain value the follower/sub goals use.
		roundCountOnLoad: { type: Boolean, default: false },
		// The dono/membership goals 404 with "Subscription required" for non-subscribers —
		// that's an expected state, not an error worth toasting.
		ignoreSubscriptionError: { type: Boolean, default: false },
	},
	data() { return {
		loading: 0,
		saving: 0,
		currencyCode: '',
		count: 0,
		countAdd: '',
		goal: 0,
		subId: null,
	}; },
	watch: {
		loading(val) { this.$emit('loading', val > 0); },
	},
	computed: {
		numberStep() { return this.numberType === 'float' ? 'any' : null; },
		unitText() { return this.unit ?? this.currencyCode; },
	},
	mounted() {
		this.loadData();
	},
	beforeDestroy() {
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		parseNum(v) {
			return this.numberType === 'float' ? (parseFloat(v || 0) || 0) : (parseInt(v || 0) || 0);
		},
		async loadData() {
			this.loading++;
			try {
				const [user, settings] = await Promise.all([
					this.$api.get('userData'),
					this.$api.get(`widgetSettings/${this.widgetType}`),
				]);
				this.currencyCode = user?.currency_code || '';
				this.goal = settings?.goal || 0;
				const rawCount = settings?.[this.countField];
				this.count = this.roundCountOnLoad ? Math.round(parseFloat(rawCount) || 0) : (rawCount || 0);

				this.subId = this.$socket.subscribe(user?.widget_hash, {
					widgetSettingsFieldChange: msg => {
						if(msg.widgetType !== this.widgetType) return;
						if(msg.field === 'goal') this.goal = msg.value;
						if(msg.field === this.countField) this.count = msg.value;
					},
				});
			} catch(e) {
				if(this.ignoreSubscriptionError && e.message === 'Subscription required') return;
				this.$bvToast.toast(`Could not load ${this.entityName} settings: ${e.error || e.toString()}`, { title: 'Error', variant: 'danger', solid: true });
			} finally {
				this.loading--;
			}
		},
		async saveField(field, value, name, add) {
			this.saving++;
			try {
				await this.$api.post(`widgetSettings/${this.widgetType}/${encodeURIComponent(field)}`, { value, add });
				if(add) this.reloadCount();
			} catch(e) {
				this.$bvToast.toast(`Could not update ${name}: ${e.error || e.toString()}`, { title: 'Error', variant: 'danger', solid: true });
			} finally {
				this.saving--;
			}
		},
		async reloadCount() {
			try {
				const settings = await this.$api.get(`widgetSettings/${this.widgetType}`);
				const rawCount = settings?.[this.countField];
				this.count = this.roundCountOnLoad ? Math.round(parseFloat(rawCount) || 0) : (rawCount || 0);
			} catch(e) { /* ignore */ }
		},
		submitGoal() {
			this.saveField('goal', this.parseNum(this.goal), 'Goal');
		},
		submitCount() {
			this.saveField(this.countField, this.parseNum(this.count), 'Count');
		},
		submitAdd() {
			this.saveField(this.countField, this.parseNum(this.countAdd), 'Count', true);
			this.countAdd = '';
		},
	},
};
</script>

<style lang="scss" scoped>
.goal-settings {
	container-type: inline-size;

	.form-control {
		min-width: 0;
	}
}

// Below this width even input + unit-text + Set/Add button no longer fit on one line —
// stack them instead of letting the row overflow. Bootstrap's connected-segment corners
// only make sense in a row, so square them off once stacked.
@container (max-width: 240px) {
	.goal-settings .input-group {
		flex-wrap: nowrap;
		flex-direction: column;
		align-items: stretch;
	}

	.goal-settings .input-group > .form-control,
	.goal-settings .input-group-append {
		width: 100%;
		border-radius: 0.2rem;
	}

	.goal-settings .input-group-append {
		margin-top: -1px;
	}
}
</style>
